export interface ExerciseQuestion {
  id: number;
  text: string;
  options: string[];
  correct_index: number;
  explanation: string;
}

export interface ExerciseSet {
  id: string;
  title: string;
  category: string;
  level: string;
  questions: ExerciseQuestion[];
  created_at?: string;
}

const STORAGE_KEY = "tef_exercise_bank_sets";

const MOCK_SETS: ExerciseSet[] = [
  {
    id: "mock-set-1",
    title: "Documentos de la vie quotidienne - Set 01",
    category: "CE",
    level: "B1",
    questions: [
      {
        id: 1,
        text: "O que o texto sugere sobre o horário de funcionamento?",
        options: ["Abre às 8h", "Abre às 9h", "Fecha às 18h"],
        correct_index: 1,
        explanation: "O texto menciona explicitamente que o estabelecimento abre às 9h."
      }
    ]
  },
  {
    id: "mock-set-2",
    title: "Compréhension Écrite - Annonce Publique",
    category: "CE",
    level: "B2",
    questions: [
      {
        id: 1,
        text: "Quel est l'objectif principal de cette annonce de travaux ?",
        options: ["Annoncer une fermeture définitive", "Informer d'une perturbation temporaire", "Recruter des ouvriers"],
        correct_index: 1,
        explanation: "L'annonce mentionne des perturbations de circulation dues à des travaux de voirie du 10 au 15 juin."
      },
      {
        id: 2,
        text: "Quelle alternative est proposée aux usagers de la route ?",
        options: ["Prendre la déviation par la rue Royale", "Utiliser uniquement les transports en commun", "Attendre la fin du chantier"],
        correct_index: 0,
        explanation: "Il est écrit d'emprunter l'itinéraire de déviation fléché via la rue Royale."
      }
    ]
  },
  {
    id: "mock-set-3",
    title: "Compréhension Orale - Message Téléphonique",
    category: "CO",
    level: "B1",
    questions: [
      {
        id: 1,
        text: "Pourquoi la secrétaire appelle-t-elle le candidat ?",
        options: ["Pour annuler l'entretien", "Pour décaler l'heure de rendez-vous", "Pour confirmer la réception de sa candidature"],
        correct_index: 1,
        explanation: "La secrétaire indique que le directeur a un contretemps et propose de décaler le rendez-vous de 14h à 15h30."
      }
    ]
  }
];

export const exerciseBankService = {
  getExerciseSets(): ExerciseSet[] {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(MOCK_SETS));
      return MOCK_SETS;
    }
    try {
      return JSON.parse(stored);
    } catch {
      return MOCK_SETS;
    }
  },

  getExerciseSetById(id: string): ExerciseSet | null {
    const sets = this.getExerciseSets();
    return sets.find(s => s.id === id) || null;
  },

  importExerciseSet(rawJson: string): ExerciseSet {
    const parsed = JSON.parse(rawJson);

    // Structural validations
    if (!parsed.title || typeof parsed.title !== "string" || !parsed.title.trim()) {
      throw new Error("O campo 'title' é obrigatório e deve ser uma string válida.");
    }
    if (!parsed.category || typeof parsed.category !== "string" || !parsed.category.trim()) {
      throw new Error("O campo 'category' é obrigatório e deve ser uma string válida.");
    }
    if (!parsed.level || typeof parsed.level !== "string" || !parsed.level.trim()) {
      throw new Error("O campo 'level' é obrigatório e deve ser uma string válida.");
    }
    if (!parsed.questions || !Array.isArray(parsed.questions) || parsed.questions.length === 0) {
      throw new Error("O campo 'questions' é obrigatório, deve ser um array e conter pelo menos uma questão.");
    }

    // Question-level validations
    parsed.questions.forEach((q: any, idx: number) => {
      if (!q.text || typeof q.text !== "string" || !q.text.trim()) {
        throw new Error(`Questão [índice ${idx}]: o campo 'text' é obrigatório e deve ser uma string.`);
      }
      if (!q.options || !Array.isArray(q.options) || q.options.length < 2) {
        throw new Error(`Questão [índice ${idx}]: o campo 'options' é obrigatório e deve ser um array com no mínimo 2 alternativas.`);
      }
      if (typeof q.correct_index !== "number" || q.correct_index < 0 || q.correct_index >= q.options.length) {
        throw new Error(`Questão [índice ${idx}]: 'correct_index' deve ser um número correspondente a um índice válido de 'options'.`);
      }
      if (!q.explanation || typeof q.explanation !== "string" || !q.explanation.trim()) {
        throw new Error(`Questão [índice ${idx}]: 'explanation' é obrigatória e deve ser uma string explicativa.`);
      }
    });

    const newSet: ExerciseSet = {
      id: "set-" + Date.now(),
      title: parsed.title.trim(),
      category: parsed.category.trim().toUpperCase(),
      level: parsed.level.trim().toUpperCase(),
      questions: parsed.questions.map((q: any, idx: number) => ({
        id: q.id || idx + 1,
        text: q.text.trim(),
        options: q.options.map((o: any) => String(o).trim()),
        correct_index: q.correct_index,
        explanation: q.explanation.trim()
      })),
      created_at: new Date().toISOString()
    };

    const sets = this.getExerciseSets();
    const updated = [newSet, ...sets];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));

    // Emit event to update catalogue
    window.dispatchEvent(new Event("exerciseSetsUpdated"));

    return newSet;
  },

  deleteExerciseSet(id: string): void {
    const sets = this.getExerciseSets();
    const filtered = sets.filter(s => s.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    window.dispatchEvent(new Event("exerciseSetsUpdated"));
  }
};
